import React, { useEffect, useState } from "react";
import {
  Card,
  Col,
  Row,
  Table,
  Typography,
  Grid,
  Spin,
  Badge,
  Statistic,
  Alert,
  Skeleton,
  Empty,
  Tabs,
  List,
  Tag,
  Space,
  Progress,
  Avatar,
} from "antd";
import {
  FileTextOutlined,
  UploadOutlined,
  SafetyCertificateOutlined,
  ClockCircleOutlined,
  BuildOutlined,
  TeamOutlined,
  PictureOutlined,
  SyncOutlined,
  CheckCircleOutlined,
  FileImageOutlined,
  BankOutlined,
} from "@ant-design/icons";
import axios from "axios";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  Legend,
} from "recharts";
import TitleComponent from "../components/TitleComponent";
import moment from "moment";
import "moment/locale/de";
moment.locale("de");

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;
const { TabPane } = Tabs;

const Dashboard = () => {
  const screens = useBreakpoint();
  const [docCount, setDocCount] = useState(0);
  const [zipCount, setZipCount] = useState(0);
  const [normsCount, setNormsCount] = useState(0);
  const [companiesCount, setCompaniesCount] = useState(0);
  const [logosCount, setLogosCount] = useState(0);
  const [lastGenerations, setLastGenerations] = useState([]);
  const [monthlyStats, setMonthlyStats] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [loading, setLoading] = useState(true);

  // Feststellen, ob wir uns im mobilen Ansichtsmodus befinden
  const isMobile = !screens.md;

  useEffect(() => {
    // Status-Prüfung (alle 10 Sekunden)
    const checkStatus = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/generation-status`,
          { withCredentials: true }
        );
        setIsGenerating(res.data.isGenerating || false);
      } catch (err) {
        console.error("Fehler beim Abrufen des Generierungsstatus:", err);
      }
    };

    // Daten laden
    const fetchDashboardData = async () => {
      setLoading(true);
      try {
        // Alle API-Aufrufe parallel ausführen
        const [
          docCountRes,
          zipRes,
          lastGenRes,
          monthlyStatsRes,
          normsRes,
          companiesRes,
          logosRes,
        ] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API_URL}/generations-count`, {
            withCredentials: true,
          }),
          axios.get(`${process.env.REACT_APP_API_URL}/files`, {
            withCredentials: true,
          }),
          axios.get(`${process.env.REACT_APP_API_URL}/generations-last`, {
            withCredentials: true,
          }),
          axios.get(`${process.env.REACT_APP_API_URL}/generations-per-month`, {
            withCredentials: true,
          }),
          axios.get(`${process.env.REACT_APP_API_URL}/norms`, {
            withCredentials: true,
          }),
          axios.get(`${process.env.REACT_APP_API_URL}/companies`, {
            withCredentials: true,
          }),
          axios.get(`${process.env.REACT_APP_API_URL}/logos`, {
            withCredentials: true,
          }),
        ]);

        // Daten setzen
        setDocCount(docCountRes.data.count || 0);
        setZipCount(zipRes.data.uploads.length || 0);
        setLastGenerations(lastGenRes.data.generations || []);
        setMonthlyStats(monthlyStatsRes.data.stats || []);
        setNormsCount(normsRes.data.norms.length || 0);
        setCompaniesCount(companiesRes.data.companies.length || 0);
        setLogosCount(logosRes.data.logos.length || 0);
      } catch (error) {
        console.error("Fehler beim Laden der Dashboard-Daten:", error);
      } finally {
        setLoading(false);
      }
    };

    // Initial laden
    fetchDashboardData();
    checkStatus();

    // Status-Polling einrichten
    const statusInterval = setInterval(checkStatus, 10000);

    // Cleanup
    return () => clearInterval(statusInterval);
  }, []);

  // Spalten für die Tabelle der letzten Generierungen
  const generateColumns = () => {
    // Spalten basierend auf Viewport-Größe anpassen
    if (isMobile) {
      return [
        {
          title: "Datum",
          dataIndex: "creation_date",
          key: "creation_date",
          render: (text) => moment(text).format("DD.MM.YY"),
        },
        {
          title: "Norm",
          dataIndex: "selected_norm",
          key: "selected_norm",
          render: (text) => <Tag color="blue">{text}</Tag>,
        },
        {
          title: "Firma",
          dataIndex: "company_name",
          key: "company_name",
          ellipsis: true,
        },
      ];
    }

    return [
      {
        title: "Erstellungsdatum",
        dataIndex: "creation_date",
        key: "creation_date",
        render: (text) => moment(text).format("DD.MM.YYYY"),
        sorter: (a, b) => new Date(a.creation_date) - new Date(b.creation_date),
      },
      {
        title: "Norm",
        dataIndex: "selected_norm",
        key: "selected_norm",
        render: (text) => <Tag color="blue">{text}</Tag>,
      },
      {
        title: "Unternehmen",
        dataIndex: "company_name",
        key: "company_name",
        sorter: (a, b) => a.company_name.localeCompare(b.company_name),
      },
    ];
  };

  // Farbpalette für Diagramme
  const COLORS = [
    "#1677ff",
    "#52c41a",
    "#fa8c16",
    "#f5222d",
    "#722ed1",
    "#13c2c2",
  ];

  // Letzte Generierungen von Normen in Kreisdiagramm visualisieren
  const prepareNormData = () => {
    if (!lastGenerations || lastGenerations.length === 0) {
      return [];
    }

    const normCount = {};
    lastGenerations.forEach((gen) => {
      if (gen.selected_norm) {
        normCount[gen.selected_norm] = (normCount[gen.selected_norm] || 0) + 1;
      }
    });

    return Object.keys(normCount).map((norm) => ({
      name: norm,
      value: normCount[norm],
    }));
  };

  // Statistik für Unternehmen mit den meisten Generierungen
  const prepareCompanyData = () => {
    if (!lastGenerations || lastGenerations.length === 0) {
      return [];
    }

    const companyCount = {};
    lastGenerations.forEach((gen) => {
      if (gen.company_name) {
        companyCount[gen.company_name] =
          (companyCount[gen.company_name] || 0) + 1;
      }
    });

    // Top 5 Unternehmen nach Generierungen
    return Object.keys(companyCount)
      .map((company) => ({ name: company, count: companyCount[company] }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);
  };

  return (
    <div
      style={{
        padding: isMobile ? "12px" : "24px",
        maxWidth: "100%",
        overflowX: "hidden",
      }}
    >
      <TitleComponent
        title="Dashboard"
        subTitle="Übersicht der Kennzahlen"
        breadcrumbItems={[
          { path: "/", label: "Home" },
          { path: "/dashboard", label: "Dashboard" },
        ]}
      />

      {isGenerating && (
        <Alert
          type="info"
          showIcon
          message="Generierung läuft..."
          description="Aktuell wird eine Dokumenten-Generation durchgeführt."
          banner
          icon={<SyncOutlined spin />}
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Statistik-Karten - 3 Zeilen für Mobile, 2 Zeilen für Tablets, 1 Zeile für Desktop */}
      <Skeleton loading={loading} active paragraph={{ rows: 2 }}>
        <Row gutter={[16, 16]}>
          <Col xs={12} sm={12} md={8} lg={4}>
            <Card
              bordered
              hoverable
              bodyStyle={{ padding: isMobile ? "12px 16px" : "24px" }}
            >
              <Statistic
                title="Dokumente"
                value={docCount}
                prefix={<FileTextOutlined style={{ color: "#1677ff" }} />}
              />
            </Card>
          </Col>

          <Col xs={12} sm={12} md={8} lg={4}>
            <Card
              bordered
              hoverable
              bodyStyle={{ padding: isMobile ? "12px 16px" : "24px" }}
            >
              <Statistic
                title="ZIP-Dateien"
                value={zipCount}
                prefix={<UploadOutlined style={{ color: "#10b981" }} />}
              />
            </Card>
          </Col>

          <Col xs={12} sm={12} md={8} lg={4}>
            <Card
              bordered
              hoverable
              bodyStyle={{ padding: isMobile ? "12px 16px" : "24px" }}
            >
              <Statistic
                title="Normen"
                value={normsCount}
                prefix={
                  <SafetyCertificateOutlined style={{ color: "#f97316" }} />
                }
              />
            </Card>
          </Col>

          <Col xs={12} sm={12} md={8} lg={4}>
            <Card
              bordered
              hoverable
              bodyStyle={{ padding: isMobile ? "12px 16px" : "24px" }}
            >
              <Statistic
                title="Unternehmen"
                value={companiesCount}
                prefix={<BankOutlined style={{ color: "#722ed1" }} />}
              />
            </Card>
          </Col>

          <Col xs={12} sm={12} md={8} lg={4}>
            <Card
              bordered
              hoverable
              bodyStyle={{ padding: isMobile ? "12px 16px" : "24px" }}
            >
              <Statistic
                title="Logos"
                value={logosCount}
                prefix={<PictureOutlined style={{ color: "#13c2c2" }} />}
              />
            </Card>
          </Col>

          <Col xs={12} sm={12} md={8} lg={4}>
            <Card
              bordered
              hoverable
              bodyStyle={{ padding: isMobile ? "12px 16px" : "24px" }}
            >
              <Statistic
                title="Gen. Status"
                value={isGenerating ? "Aktiv" : "Inaktiv"}
                valueStyle={{ color: isGenerating ? "#1677ff" : "#52c41a" }}
                prefix={
                  isGenerating ? <SyncOutlined spin /> : <CheckCircleOutlined />
                }
              />
            </Card>
          </Col>
        </Row>
      </Skeleton>

      {/* Diagramm + Tabelle mit Tabs für Mobile */}
      <div style={{ marginTop: 16 }}>
        {isMobile ? (
          <Skeleton loading={loading} active paragraph={{ rows: 10 }}>
            <Tabs defaultActiveKey="1">
              <TabPane tab="Statistik" key="1">
                <Card
                  title="Generierungen pro Monat"
                  bodyStyle={{ padding: "12px" }}
                >
                  <ResponsiveContainer width="100%" height={250}>
                    <LineChart
                      data={monthlyStats}
                      margin={{ top: 5, right: 5, left: 0, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                      <YAxis width={25} tick={{ fontSize: 12 }} />
                      <Tooltip />
                      <Line
                        type="monotone"
                        dataKey="count"
                        name="Anzahl"
                        stroke="#1677ff"
                        strokeWidth={2}
                        dot
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </Card>

                <Card
                  title="Top Normen"
                  style={{ marginTop: 16 }}
                  bodyStyle={{ padding: "12px" }}
                >
                  {prepareNormData().length > 0 ? (
                    <ResponsiveContainer width="100%" height={200}>
                      <PieChart>
                        <Pie
                          data={prepareNormData()}
                          cx="50%"
                          cy="50%"
                          innerRadius={30}
                          outerRadius={60}
                          paddingAngle={5}
                          dataKey="value"
                          nameKey="name"
                          label={(entry) => entry.name}
                          labelLine={false}
                        >
                          {prepareNormData().map((entry, index) => (
                            <Cell
                              key={`cell-${index}`}
                              fill={COLORS[index % COLORS.length]}
                            />
                          ))}
                        </Pie>
                        <Tooltip
                          formatter={(value) => [`${value} Generierungen`, ""]}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description="Keine Daten verfügbar"
                    />
                  )}
                </Card>

                <Card
                  title="Top Unternehmen"
                  style={{ marginTop: 16 }}
                  bodyStyle={{ padding: "12px" }}
                >
                  {prepareCompanyData().length > 0 ? (
                    <List
                      size="small"
                      dataSource={prepareCompanyData()}
                      renderItem={(item, index) => (
                        <List.Item key={item.name}>
                          <List.Item.Meta
                            avatar={
                              <Avatar
                                style={{
                                  backgroundColor:
                                    COLORS[index % COLORS.length],
                                }}
                              >
                                {index + 1}
                              </Avatar>
                            }
                            title={item.name}
                          />
                          <div>
                            <Tag color={COLORS[index % COLORS.length]}>
                              {item.count}
                            </Tag>
                          </div>
                        </List.Item>
                      )}
                    />
                  ) : (
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description="Keine Daten verfügbar"
                    />
                  )}
                </Card>
              </TabPane>

              <TabPane tab="Letzte Aktivitäten" key="2">
                <Card
                  title="Letzte Generierungen"
                  bodyStyle={{ padding: "12px" }}
                >
                  <Table
                    dataSource={lastGenerations.map((item, index) => ({
                      ...item,
                      key: index,
                    }))}
                    columns={generateColumns()}
                    pagination={{
                      size: "small",
                      pageSize: 5,
                      hideOnSinglePage: true,
                    }}
                    size="small"
                    scroll={{ x: "100%" }}
                  />
                </Card>
              </TabPane>
            </Tabs>
          </Skeleton>
        ) : (
          // Desktop Ansicht
          <Skeleton loading={loading} active paragraph={{ rows: 10 }}>
            <Row gutter={[16, 16]} style={{ marginTop: 8 }}>
              {/* Linke Seite: Statistik-Grafiken */}
              <Col xs={24} lg={14}>
                <Card title="Generierungen pro Monat">
                  <ResponsiveContainer width="100%" height={250}>
                    <LineChart data={monthlyStats}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis />
                      <Tooltip />
                      <Line
                        type="monotone"
                        dataKey="count"
                        name="Anzahl"
                        stroke="#1677ff"
                        strokeWidth={3}
                        dot
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </Card>

                <Row gutter={16} style={{ marginTop: 16 }}>
                  <Col span={12}>
                    <Card title="Top Normen">
                      {prepareNormData().length > 0 ? (
                        <ResponsiveContainer width="100%" height={200}>
                          <PieChart>
                            <Pie
                              data={prepareNormData()}
                              cx="50%"
                              cy="50%"
                              innerRadius={40}
                              outerRadius={70}
                              paddingAngle={5}
                              dataKey="value"
                              nameKey="name"
                              label
                            >
                              {prepareNormData().map((entry, index) => (
                                <Cell
                                  key={`cell-${index}`}
                                  fill={COLORS[index % COLORS.length]}
                                />
                              ))}
                            </Pie>
                            <Tooltip
                              formatter={(value) => [
                                `${value} Generierungen`,
                                "",
                              ]}
                            />
                            <Legend />
                          </PieChart>
                        </ResponsiveContainer>
                      ) : (
                        <Empty
                          image={Empty.PRESENTED_IMAGE_SIMPLE}
                          description="Keine Daten verfügbar"
                        />
                      )}
                    </Card>
                  </Col>
                  <Col span={12}>
                    <Card title="Top Unternehmen">
                      {prepareCompanyData().length > 0 ? (
                        <ResponsiveContainer width="100%" height={200}>
                          <BarChart
                            data={prepareCompanyData()}
                            layout="vertical"
                            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis type="number" />
                            <YAxis
                              type="category"
                              dataKey="name"
                              width={100}
                              tick={{ fontSize: 11 }}
                            />
                            <Tooltip />
                            <Bar
                              dataKey="count"
                              name="Generierungen"
                              fill="#1677ff"
                              barSize={20}
                            />
                          </BarChart>
                        </ResponsiveContainer>
                      ) : (
                        <Empty
                          image={Empty.PRESENTED_IMAGE_SIMPLE}
                          description="Keine Daten verfügbar"
                        />
                      )}
                    </Card>
                  </Col>
                </Row>
              </Col>

              {/* Rechte Seite: Tabelle und Listen */}
              <Col xs={24} lg={10}>
                <Card title="Letzte Generierungen">
                  <Table
                    dataSource={lastGenerations.map((item, index) => ({
                      ...item,
                      key: index,
                    }))}
                    columns={generateColumns()}
                    pagination={{ pageSize: 7 }}
                    size="small"
                  />
                </Card>
              </Col>
            </Row>
          </Skeleton>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
